// Pure UI-language helpers. This module imports only typesafe-i18n's generated
// locale set (`isLocale` / `baseLocale`) and the equally pure `couch-session-hint`
// (localStorage + JSON, no Angular / Firebase / Capacitor), so it is still safe to
// run at i18n module-load time (before the app bootstraps) and easy to reason
// about in isolation. It is the single home for the device language key and the
// locale-resolution rules shared by the i18n service, the settings switcher, the
// first-run default, and the sign-in reconciliation.

import type { Locales } from '../../gensrc/i18n/i18n-types';
import { baseLocale, isLocale } from '../../gensrc/i18n/i18n-util';
import { getCouchSessionHint } from '../services/couch-session-hint';

/**
 * Device-scoped localStorage key holding the last-applied UI language on THIS
 * device — a boot-time cache. It is registered in local-storage-hygiene's
 * DEVICE_LEVEL_LOCALSTORAGE_KEYS so it SURVIVES sign-out, which lets the
 * signed-out / onboarding screens render in the chosen language and lets the
 * i18n module read the language synchronously at boot (before any user doc is
 * loaded). The authoritative, cross-device, per-user preference is
 * `settings.uiLanguage`; that value WINS on sign-in via the reconciliation in
 * Stage C. The literal is duplicated (with a cross-check test) in
 * local-storage-hygiene.ts to keep that module dependency-free.
 */
export const CS_UI_LANGUAGE_KEY = 'cs_ui_language';

/**
 * Device-scoped localStorage key recording WHICH user chose the language stored
 * in `cs_ui_language`. Written whenever we know the signed-in uid: by the
 * Settings switch, and by the boot reconciliation when it settles on a language
 * for the current user.
 *
 * It exists to disambiguate an empty `settings.uiLanguage`, which otherwise
 * means two opposite things — see `decideUiLanguageReconcile` below.
 *
 * Like `cs_ui_language` itself it is registered in local-storage-hygiene's
 * DEVICE_LEVEL_LOCALSTORAGE_KEYS so it SURVIVES sign-out. That is not an
 * oversight: the case it exists to serve is precisely "user A signed out, user B
 * signs in", and a key wiped on sign-out would be gone exactly when it is
 * needed. The literal is duplicated (with a cross-check test) in
 * local-storage-hygiene.ts to keep that module dependency-free.
 */
export const CS_UI_LANGUAGE_UID_KEY = 'cs_ui_language_uid';

/**
 * Device-scoped localStorage key holding a language the user picked HERE that
 * `settings.uiLanguage` has not confirmed yet. Written by the Settings switch,
 * cleared as soon as the settings doc comes back agreeing with it.
 *
 * It is the RECENCY signal the reconciliation would otherwise lack. Knowing WHO
 * chose the device language (`cs_ui_language_uid`) is enough to protect a choice
 * from an EMPTY settings value, but not from a STALE one: a settings doc still
 * holding the previous language is a valid locale, so the old rule adopted it
 * and undid a switch the user had made seconds earlier. That is why switching
 * away from a language could fail even after the owner check existed.
 *
 * While a choice is unconfirmed it outranks `settings.uiLanguage`. Once
 * confirmed the flag goes away and `settings.uiLanguage` is authoritative again
 * — which is what keeps a language changed on ANOTHER device propagating here.
 *
 * The stored VALUE names its own chooser — `"<uid>:<locale>"`, built by
 * `encodeUnconfirmedChoice`. That is load-bearing, not decoration. All three
 * device keys survive sign-out, so if the marker held a bare locale it could be
 * INHERITED: user A picks Spanish and signs out before it is confirmed, user B
 * signs in and the reconciliation transfers ownership of the device key to B —
 * and B would then be treated as having a pending Spanish choice. The next time
 * B changed language on another device, this device would keep Spanish AND heal
 * it back into B's account, destroying the change everywhere. Binding the uid
 * into the value makes that unrepresentable rather than relying on every
 * ownership-transfer path remembering to clear it.
 *
 * Registered in local-storage-hygiene's DEVICE_LEVEL_LOCALSTORAGE_KEYS beside
 * the other two, so a sign-out cannot strand a half-applied switch. The literal
 * is duplicated (with a cross-check test) in local-storage-hygiene.ts to keep
 * that module dependency-free.
 */
export const CS_UI_LANGUAGE_UNCONFIRMED_KEY = 'cs_ui_language_unconfirmed';

/** Build the `cs_ui_language_unconfirmed` value for a language this user just picked. */
export function encodeUnconfirmedChoice(uid: string, locale: string): string {
  return `${uid}:${locale}`;
}

/**
 * Read back an `encodeUnconfirmedChoice` value, but ONLY for the user it names.
 * Anything else — a marker left by a different account, a malformed value, or
 * nothing stored — yields null, i.e. "no pending choice", which is the safe
 * default (the account's own preference then decides as it always did).
 */
function unconfirmedChoiceFor(stored: string | null, currentUid: string): string | null {
  if (stored == null) return null;
  const separator = stored.indexOf(':');
  if (separator < 0) return null;
  if (stored.slice(0, separator) !== currentUid) return null;
  return stored.slice(separator + 1);
}

/**
 * What the boot reconciliation should do with the device language key.
 *
 * A discriminated union rather than one shape with an optional `target`: the
 * client tsconfig sets no `strictNullChecks`, so an `apply` that forgot to set
 * `target` would compile and write the string "undefined" into the device key,
 * which then fails `isLocale` on every later boot. This makes that
 * unrepresentable.
 */
export type UiLanguageReconcileDecision =
  | {
    /** Leave the device key and the visible language exactly as they are. */
    action: 'keep';
    /** Plain-language explanation, logged by the caller so a decision is never silent. */
    reason: string;
    /**
     * Set when the caller should record the CURRENT user as the owner of the
     * device language. Only true when that claim is actually true — never for a
     * device key this user did not choose, and never when there is no key to own.
     */
    claimOwnership: boolean;
    /**
     * Set when this device holds a language the user chose here that their saved
     * preference does not yet reflect — either because it is EMPTY, or because it
     * is STALE, still naming the language they just switched away from. The caller
     * writes it back to `settings.uiLanguage`.
     *
     * Note it therefore CAN overwrite a non-empty account preference. That is
     * deliberate and is the point of the unconfirmed marker: while a local choice
     * is unconfirmed it is the more recent statement of intent. It stops being
     * able to do so the moment the marker clears, which is what keeps a language
     * changed on another device winning.
     */
    healSavedPreferenceTo?: Locales;
    /**
     * Set when the settings doc has caught up with a locally-chosen language, so
     * the caller should drop the unconfirmed marker. After that
     * `settings.uiLanguage` is authoritative again and a change made on another
     * device propagates here normally.
     */
    clearUnconfirmed?: boolean;
  }
  | {
    /** Overwrite the device key with `target` and reload once. */
    action: 'apply';
    /** The locale to bind to. Always present on this branch. */
    target: Locales;
    reason: string;
    /** Always true: the caller has just resolved this language FOR the current user. */
    claimOwnership: true;
  };

/** One localStorage write the caller must perform to carry out a decision. */
export type UiLanguageStorageOp =
  | { op: 'set'; key: string; value: string }
  | { op: 'remove'; key: string };

/**
 * The exact localStorage writes a decision implies, as data.
 *
 * This exists so the SIDE EFFECTS are testable, not just the decision. The whole
 * safety argument of the unconfirmed marker is "it can never permanently outrank
 * the account", and that holds only if the caller actually clears it — but the
 * caller is a private method on the Angular app component with no test harness.
 * Deleting its clear-the-marker line would have left every unit test green while
 * every device silently stopped accepting a language changed elsewhere. Returning
 * the writes as data closes that gap.
 *
 * The `apply` branch's device-key write is deliberately NOT included: it must
 * happen below the caller's one-reload-per-session guard, so the caller owns its
 * ordering. This covers the writes that are unconditional for a given decision.
 */
export function uiLanguageStorageOpsFor(
  decision: UiLanguageReconcileDecision,
  currentUid: string,
): UiLanguageStorageOp[] {
  const ops: UiLanguageStorageOp[] = [];
  if (decision.action === 'keep') {
    if (decision.claimOwnership) {
      ops.push({ op: 'set', key: CS_UI_LANGUAGE_UID_KEY, value: currentUid });
    }
    if (decision.clearUnconfirmed) {
      ops.push({ op: 'remove', key: CS_UI_LANGUAGE_UNCONFIRMED_KEY });
    }
    return ops;
  }
  // apply: the device language is being replaced by one resolved from the
  // account, so this user owns it and no local choice is left pending.
  ops.push({ op: 'set', key: CS_UI_LANGUAGE_UID_KEY, value: currentUid });
  ops.push({ op: 'remove', key: CS_UI_LANGUAGE_UNCONFIRMED_KEY });
  return ops;
}

/**
 * Decide whether the boot reconciliation should overwrite this device's language
 * key with a different language, or leave the user where they are.
 *
 * The hard part is that an EMPTY `savedPreference` means two OPPOSITE things:
 *
 *  1. "This user has never chosen a language." That is the shared-device case —
 *     user A picked Spanish and signed out (the device key deliberately survives
 *     sign-out), user B signs in and must NOT be stranded in A's Spanish.
 *  2. "This user chose a language seconds ago on THIS device, and it has not
 *     reached their settings doc yet." Resetting here silently undoes a
 *     deliberate action — the bug this helper was written to fix, hit on an
 *     account whose settings doc was slow to settle because of sync conflicts.
 *
 * Reading emptiness alone cannot tell those apart, so we do not try: the device
 * key records WHO chose it, and only a key belonging to somebody else is treated
 * as a leftover. An unknown owner (an install predating that key) still resets,
 * so the change fails safe toward the protection that already existed.
 *
 * Ownership alone is still not enough, because `savedPreference` can be STALE
 * rather than empty — a valid locale that is simply the language the user just
 * switched AWAY from. Adopting it undoes the switch just as surely. So a choice
 * made on this device also carries `unconfirmedChoice` until the settings doc
 * agrees with it, and while unconfirmed it outranks the doc. That marker is what
 * makes the rule symmetric: en -> es and es -> en behave the same. It clears on
 * confirmation, so a language changed on ANOTHER device still wins here.
 *
 * Pure on purpose — `environmentDefault` is passed in rather than read from
 * `navigator` — so every branch is unit-testable. See i18n.test.ts.
 */
export function decideUiLanguageReconcile(params: {
  /** the per-user `settings.uiLanguage`; empty string when never chosen */
  savedPreference: string | undefined;
  /** the device-scoped `cs_ui_language` value, or null when absent */
  deviceKey: string | null;
  /** the `cs_ui_language_uid` value: who chose `deviceKey`, or null when unknown */
  deviceKeyOwnerUid: string | null;
  /**
   * the raw `cs_ui_language_unconfirmed` value — a `"<uid>:<locale>"` marker for a
   * language chosen on this device that `savedPreference` has not confirmed yet,
   * or null when there is none. It is decoded against `currentUid`, so a marker
   * belonging to another account is ignored rather than inherited.
   */
  unconfirmedChoice: string | null;
  /** the uid of the user who just signed in */
  currentUid: string;
  /** the locale `LL` is ACTUALLY bound to this session (BOOT_LOCALE) */
  boundLocale: Locales;
  /** what `getEnvironmentDefaultLocale()` returns — passed in to keep this pure */
  environmentDefault: Locales;
}): UiLanguageReconcileDecision {
  // Narrowed once and reused: `isLocale` is a type guard, so capturing the
  // narrowed value here means the rest of the function can use `savedPreference`
  // as a `Locales` without re-testing it (the guard does not narrow the property
  // through a plain boolean const).
  const savedPreference = isLocale(params.savedPreference) ? params.savedPreference : null;

  // An unconfirmed local choice outranks the settings doc — see
  // CS_UI_LANGUAGE_UNCONFIRMED_KEY above for why ownership alone is not enough.
  const pendingChoice = unconfirmedChoiceFor(params.unconfirmedChoice, params.currentUid);
  if (params.deviceKeyOwnerUid === params.currentUid
    && isLocale(params.deviceKey)
    && pendingChoice === params.deviceKey) {
    if (savedPreference === params.deviceKey) {
      return {
        action: 'keep',
        reason: `this device's language "${params.deviceKey}" is now confirmed by the saved settings`,
        claimOwnership: true,
        clearUnconfirmed: true,
      };
    }
    return {
      action: 'keep',
      reason: `keeping this device's language "${params.deviceKey}" — chosen here and not yet confirmed by the saved settings (which say "${params.savedPreference || 'nothing'}")`,
      claimOwnership: true,
      healSavedPreferenceTo: params.deviceKey,
    };
  }

  if (savedPreference == null) {
    // Never expressed a language on this device either — leave English as-is. Do
    // NOT auto-switch an existing user to their browser language; the first-run
    // default (location.service) handles brand-new installs only. Nothing to own
    // here: there is no device language to attribute to anyone.
    if (params.deviceKey == null) {
      return {
        action: 'keep',
        reason: 'no saved language and no device language — leaving the app as it is',
        claimOwnership: false,
      };
    }
    // The device language is THIS user's own recent choice. Their settings doc has
    // not caught up yet, which is a sync delay, not a missing preference.
    //
    // `isLocale(params.deviceKey)` matters as well as the uid match: a key holding
    // something we do not ship (hand-edited, or a locale we retired) would
    // otherwise be kept forever, where the old rule cleaned it up via the
    // environment-default path below.
    if (params.deviceKeyOwnerUid != null
      && params.deviceKeyOwnerUid === params.currentUid
      && isLocale(params.deviceKey)) {
      return {
        action: 'keep',
        reason: `keeping this device's language "${params.deviceKey}" — this user chose it here and their saved settings have not synced yet`,
        claimOwnership: true,
        // Write it back to the account, or the device keeps them in their language
        // while their account forgets it ever happened.
        healSavedPreferenceTo: params.deviceKey,
      };
    }
  }

  // `savedPreference` wins when it is a language we ship. Otherwise we are in the
  // shared-device / unknown-owner case and fall back to the environment default,
  // which is what un-sticks a second user from the first user's language.
  const target: Locales = savedPreference ?? params.environmentDefault;

  if (target === params.boundLocale) {
    return {
      action: 'keep',
      reason: `already showing "${target}" — nothing to change`,
      // Only claim the device language when it really is this user's: with a saved
      // preference the visible language IS their preference. Without one we are
      // here by coincidence (the environment default happens to match a key
      // somebody else chose), and stamping ownership would record a choice this
      // user never made — which is what the reset relies on to spot a leftover.
      // `deviceKey != null` as well, so we never record an owner for a key that
      // does not exist.
      claimOwnership: savedPreference != null && params.deviceKey != null,
    };
  }

  return {
    action: 'apply',
    target,
    claimOwnership: true,
    reason: savedPreference != null
      ? `switching to this user's saved language "${target}" (was showing "${params.boundLocale}")`
      // Three different inputs reach this reset, and a log that conflates them
      // sends a debugger looking for an account that may not exist — so name
      // which one it actually was.
      : params.deviceKeyOwnerUid == null
        ? `resetting this device's language to "${target}" — "${params.deviceKey}" has no recorded owner (was showing "${params.boundLocale}")`
        : params.deviceKeyOwnerUid === params.currentUid
          ? `resetting this device's language to "${target}" — this user owns "${params.deviceKey}" but it is not a language we ship (was showing "${params.boundLocale}")`
          : `resetting this device's language to "${target}" — "${params.deviceKey}" was left by a different user (was showing "${params.boundLocale}")`,
  };
}

/**
 * Resolve the UI language for this app session, synchronously, at boot. Three
 * cases, in order:
 *
 *  1. A saved device language (`cs_ui_language`) that is a supported locale wins.
 *     This covers everyone who has ever chosen a language or had one assigned.
 *  2. Otherwise, if this device holds an established signed-in CouchDB session
 *     (the `couch-session-hint` marker — synchronous, and wiped on sign-out),
 *     fall back to English. This PROTECTS a logged-in user: the browser default
 *     below must never reach an established account and flip, say, an English
 *     user with a French phone to French on Home. Their language comes from their
 *     account (`settings.uiLanguage`); an empty saved language means "established
 *     English", so English is right here.
 *  3. Otherwise — no saved language AND no signed-in account, i.e. a genuinely
 *     logged-out or fresh install — default to the browser language. A first-run
 *     non-English user then sees login/subscribe in their language immediately,
 *     and once they sign in the locale-scoped `global_common` sync (#4284) fetches
 *     that language from the start rather than English-first. `firstRunLanguageDefault`
 *     persists the choice into `settings.uiLanguage` + `cs_ui_language` at account
 *     setup, so this stays a session default until the account stamps it.
 *
 * Runs at i18n module-load time (imported by ~136 files), so every storage read
 * is guarded for the node unit-test / SSR context and it never throws when there
 * is no browser storage.
 */
export function resolveBootLocale(): Locales {
  if (typeof localStorage === 'undefined') return baseLocale;
  const stored = localStorage.getItem(CS_UI_LANGUAGE_KEY);
  if (stored && isLocale(stored)) return stored;
  // an established signed-in account keeps English rather than the browser guess
  if (getCouchSessionHint() != null) return baseLocale;
  // genuinely logged-out / fresh install: greet the user in their own language
  return getEnvironmentDefaultLocale();
}

/**
 * The UI language for THIS app session, resolved ONCE at module load.
 *
 * Read this — not a fresh `resolveBootLocale()` call — whenever you need "the
 * language the app is actually showing". The two can differ: the first-run
 * language default (`location.service.ts`) writes `cs_ui_language` without
 * reloading immediately, so a later re-read can return a language that `LL` is
 * not bound to yet. Changing the language in Settings reloads the whole app,
 * which is what makes a session-fixed value correct.
 *
 * It lives here, in the Angular-free module, so non-Angular helpers (e.g.
 * `services/text-locale.ts`) can use it too. `i18n/i18n.service.ts` re-exports
 * it, which is where most of the app imports it from.
 */
export const BOOT_LOCALE: Locales = resolveBootLocale();

/**
 * Pick the best supported locale from a list of BCP-47 language tags (e.g.
 * `['es-419', 'en-US']`), matching on the primary subtag only. Falls back to
 * the base locale when nothing matches. When no explicit list is passed, it
 * reads the browser's `navigator.languages` (then `navigator.language`). Used
 * by the first-run default and the no-preference reconciliation branch.
 */
export function getEnvironmentDefaultLocale(candidateLanguages?: readonly string[]): Locales {
  let candidates: readonly string[] = candidateLanguages ?? [];
  if (candidates.length === 0 && typeof navigator !== 'undefined') {
    candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
  }
  for (const lang of candidates) {
    if (!lang) continue;
    const primary = lang.toLowerCase().split('-')[0]; // 'es-419' → 'es'
    if (isLocale(primary)) return primary;
  }
  return baseLocale;
}

/**
 * The three raw UI-language signals we report to analytics (#4147 telemetry).
 * All three are RAW values — no derived booleans. Anything comparative (e.g.
 * "is the app showing English while the device wants another language?", or "is
 * the device language one we actually ship?") is derivable from these at query
 * time, so we deliberately do NOT store those flags. See design-docs/telemetry.md.
 */
export type UiLanguageTelemetryProps = {
  // the language the app is ACTUALLY showing this session — a supported locale.
  user_ui_language: string;
  // the device / browser UI language, as its RAW primary subtag (e.g. 'pt', 'de'),
  // NOT clamped to the languages we support. This is the unmet-demand signal: a
  // Portuguese user reads 'pt' here even though we ship no Portuguese. Empty
  // string when the environment exposes no language.
  user_ui_language_browser: string;
  // the default language of the user's country of location, as its primary
  // subtag (e.g. 'es' for Spain). Taken from the geo lookup's ranked language
  // list (GeoIPInfo.languages, e.g. 'es,eu,ca,gl,oc'). Empty string when geo is
  // unknown.
  user_ui_language_country: string;
};

/**
 * Reduce a BCP-47 tag to its lower-cased primary subtag (`'es-419'` → `'es'`),
 * or `''` for an absent/blank tag. Shared by the browser + country extractors.
 */
function primaryLanguageSubtag(tag: string | null | undefined): string {
  if (!tag) return '';
  return tag.toLowerCase().split('-')[0].trim();
}

/**
 * Build the three raw UI-language telemetry signals (#4147). Pure and
 * unit-tested: the browser candidate list and the country language list are
 * passed in by the caller (so this needs no navigator / settings access),
 * defaulting to `navigator.languages` only when no list is supplied.
 *
 * - `user_ui_language` is the passed active locale (what `LL` is bound to).
 * - `user_ui_language_browser` is the RAW primary subtag of the first candidate
 *   language — unclamped, so it surfaces demand for languages we do not ship.
 * - `user_ui_language_country` is the primary subtag of the first entry of the
 *   country's geo language list (a comma-separated `GeoIPInfo.languages`).
 */
export function buildUiLanguageTelemetryProps(
  activeLocale: Locales,
  opts?: { candidateLanguages?: readonly string[]; countryLanguages?: string | null },
): UiLanguageTelemetryProps {
  let candidates: readonly string[] = opts?.candidateLanguages ?? [];
  if (!opts?.candidateLanguages && typeof navigator !== 'undefined') {
    candidates = navigator.languages?.length ? navigator.languages : navigator.language ? [navigator.language] : [];
  }
  const firstCandidate = candidates.find((lang) => !!lang);

  // the geo list is a comma-separated, rank-ordered set of the country's
  // languages (e.g. Spain → 'es,eu,ca,gl,oc'); the first entry is its default.
  const firstCountryLang = (opts?.countryLanguages ?? '').split(',')[0];

  return {
    user_ui_language: activeLocale,
    user_ui_language_browser: primaryLanguageSubtag(firstCandidate),
    user_ui_language_country: primaryLanguageSubtag(firstCountryLang),
  };
}

/**
 * Decide the first-run UI-language default (#4147). Returns the locale to adopt,
 * or `null` to leave the language untouched. The default applies ONLY on a
 * genuine new install (`isInitialSetup`) AND only when the user has not already
 * chosen a language (`currentUiLanguage` empty). Gating on `isInitialSetup`
 * keeps it away from existing users whose locale re-initializes on a geo change
 * or a settings-version bump — they keep English until they choose in Settings.
 * This is the pure, unit-tested core of the location-service first-run block.
 */
export function firstRunLanguageDefault(
  isInitialSetup: boolean,
  currentUiLanguage: string,
  candidateLanguages?: readonly string[],
): Locales | null {
  if (!isInitialSetup || currentUiLanguage) return null;
  return getEnvironmentDefaultLocale(candidateLanguages);
}
